import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { readStoredJson } from "../utilities/session";
import {
  FaRegBuilding,
  FaUserShield,
  FaHistory,
  FaCheckCircle,
} from "react-icons/fa";
import { IoIosBusiness } from "react-icons/io";
import { IoPeopleSharp } from "react-icons/io5";
import { LuWallet, LuCoins } from "react-icons/lu";
import { CiSettings, CiViewList } from "react-icons/ci";
import { MdOutlineSecurity, MdOutlineManageAccounts, MdOutlineDescription } from "react-icons/md";
import { GrPlan } from "react-icons/gr";
import { FaUserTie } from "react-icons/fa6";
import { RiLogoutBoxLine } from "react-icons/ri";
import { useSettings } from "../context/SettingsContext";
import { useCompanyContext } from "../context/CompanyContext";
import { getSidebarConfig } from "../api/dashboardApi";
import { getMyOfficePortals } from "../api/officePortalApi";
import {
  getDashboardPathForCompany,
  setCurrentCompanyId,
} from "../utilities/companyNavigation";
import { isExistingAdmin } from "../utilities/userAccess";

const ChevronDown = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.2s",
    }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const LeftSideBar = ({ isOpen, user }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { globalSettings } = useSettings();
  const { currentCompanyId } = useCompanyContext();

  const [sidebarData, setSidebarData] = useState({
    mother_company: null,
    sister_companies: [],
  });
  const [officePortals, setOfficePortals] = useState([]);

  const [expandedMenu, setExpandedMenu] = useState({});

  useEffect(() => {
    const fetchSidebarConfig = async () => {
      try {
        const [data, portals] = await Promise.all([
          getSidebarConfig(),
          getMyOfficePortals().catch(() => []),
        ]);
        setSidebarData(data);
        setOfficePortals(portals);

        const initialExpanded = {};
        if (data.mother_company) {
          initialExpanded[`company_${data.mother_company.id}`] = true;
        }
        data.sister_companies?.forEach((c) => {
          initialExpanded[`company_${c.id}`] = true;
        });
        setExpandedMenu((prev) => ({ ...prev, ...initialExpanded }));
      } catch (err) {
      }
    };
    fetchSidebarConfig();
  }, [currentCompanyId]);

  const getLogo = () => {
    if (globalSettings?.logo) return globalSettings.logo;
    const savedSettings = readStoredJson("systemSettings", null);
    if (savedSettings?.logo) return savedSettings.logo;
    return "/assets/images/logo.png";
  };

  const onLogout = () => {
    dispatch(logout()); // clears the stored session too
    navigate("/", { replace: true });
    window.location.reload();
  };

  const isAdmin = isExistingAdmin(user);

  const isUser = user?.role?.toLowerCase() === "user";

  const toggleSubMenu = (key) => {
    setExpandedMenu((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const goToCompanyDashboard = (company) => {
    setCurrentCompanyId(company.id);
    const path = getDashboardPathForCompany(
      company,
      sidebarData.mother_company?.id,
    );
    navigate(path);
  };

  const goToCompanyApprovals = (companyId) => {
    setCurrentCompanyId(companyId);
    navigate(`/approver-dashboard?company_id=${companyId}`);
  };

  const renderCompanySection = (company, isMother = false) => {
    const key = `company_${company.id}`;
    const rolesLabel =
      company.roles?.length > 0 ? company.roles.join(", ") : "Member";

    return (
      <div key={company.id}>
        <div
          className="menu-item company-menu-header"
          onClick={() => toggleSubMenu(key)}
          title={`${company.name} (${rolesLabel})`}
        >
          <div className="company-menu-label">
            {isMother ? <FaRegBuilding /> : <IoIosBusiness />}
            <span className="company-menu-name">{company.name}</span>
          </div>
          <span className="company-menu-chevron">
            <ChevronDown open={expandedMenu[key]} />
          </span>
        </div>

        {expandedMenu[key] && (
          <div className="submenu open" style={{ paddingLeft: "20px" }}>
            <div
              className="menu-item"
              onClick={() => goToCompanyDashboard(company)}
            >
              <CiViewList /> Dashboard
            </div>
            {company.can_approve && (
              <div
                className="menu-item"
                onClick={() => goToCompanyApprovals(company.id)}
              >
                <FaCheckCircle /> Pending Approvals
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebar-header" onClick={() => navigate("/")}>
        <img src={getLogo()} alt="logo" className="logo-img" />
      </div>

      <div className="sidebar-menu">
        {isAdmin && (
          <div className="menu-item" onClick={() => navigate("/dashboard")}>
            <CiViewList /> Admin Dashboard
          </div>
        )}

        {(sidebarData.mother_company ||
          sidebarData.sister_companies.length > 0) && (
          <div className="menu-category">My Companies</div>
        )}

        {sidebarData.mother_company &&
          renderCompanySection(sidebarData.mother_company, true)}

        {sidebarData.sister_companies.map((company) =>
          renderCompanySection(company),
        )}

        {officePortals.length > 0 && (
          <>
            <div className="menu-category">Office Portals</div>
            {officePortals.map((portal) => (
              <div
                key={portal.id}
                className="menu-item"
                onClick={() => navigate(`/office-portal/${portal.portal_slug}`)}
                title={`${portal.name} — ${portal.company?.name}`}
              >
                <FaRegBuilding /> {portal.name}
              </div>
            ))}
          </>
        )}

        <div className="menu-category">Account</div>
        <div className="menu-item" onClick={() => navigate("/wallet")}>
          <LuWallet /> My Wallet
        </div>
        <div className="menu-item" onClick={() => navigate("/profile")}>
          <IoPeopleSharp /> Profile
        </div>

        {isUser && !isAdmin && (
          <>
            <div className="menu-category">Role Actions</div>
            <div
              className="menu-item"
              onClick={() => navigate("/role_selection")}
            >
              <MdOutlineManageAccounts /> Request New Role
            </div>
            <div className="menu-item" onClick={() => navigate("/role-requests")}>
              <FaHistory /> Request History
            </div>
          </>
        )}

        {isAdmin && (
          <>
            <div className="menu-category">System Control</div>
            <div className="menu-item" onClick={() => navigate("/user-roles")}>
              <FaUserTie /> User Role Assign
            </div>
            <div className="menu-item" onClick={() => navigate("/manage-roles")}>
              <FaUserShield /> Role Definitions
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/admin/mlm-levels")}
            >
              <GrPlan /> Tree Levels
            </div>
            <div className="menu-item" onClick={() => navigate("/admin/mlm-tree")}>
              <IoIosBusiness /> Tree Nodes
            </div>
            <div className="menu-item" onClick={() => navigate("/admin/offices")}>
              <FaRegBuilding /> Offices
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/point-exchange-rates")}
            >
              <LuCoins /> Point Settings
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/companies-manage")}
            >
              <FaRegBuilding /> Companies
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/security-management")}
            >
              <MdOutlineSecurity /> Security
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/system-settings")}
            >
              <CiSettings /> System Settings
            </div>
            <div
              className="menu-item"
              onClick={() => navigate("/admin/api-docs")}
            >
              <MdOutlineDescription /> API Docs
            </div>
          </>
        )}
      </div>

      <hr className="mx-3 opacity-25" />

      <button onClick={onLogout} className="logout-btn mb-4">
        <RiLogoutBoxLine />
        <span>Log Out</span>
      </button>
    </aside>
  );
};

export default LeftSideBar;
